import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { savePersonalDataByKey } from '../../../../features/personalData/personalData-slice';
import { SignInVerification } from '../../../../entities/SignInMethod';
import { useUserVerifyMutation } from '../../../../services/singnin.service';
import { dynamicCustomEventTracking } from '../../../../lib/gtm';
import CpfStepModel from './model';

export type CpfStepFormFields = {
  oyana: {
    cpf: string;
  };
};

type CpfStepViewModelProps = {
  onUserVerified: (data: SignInVerification) => void;
  onLoadingChange: (isLoading: boolean) => void;
  onContinueWithoutLogin?: () => void;
};

export default function CpfStepViewModel({
  onUserVerified,
  onLoadingChange,
  onContinueWithoutLogin
}: CpfStepViewModelProps) {
  const model = CpfStepModel;
  const form = useForm<CpfStepFormFields>();
  const dispatch = useDispatch();
  const [cpf, setCpf] = useState('');
  const [isValid, setIsValid] = useState(false);

  const [verifyUser, { data: verifyData, error: verifyError, isLoading: verifyIsLoading }] = useUserVerifyMutation();

  const isLoading = verifyIsLoading;
  const hasError = !!form.formState.errors.oyana?.cpf;

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isValid && cpf) {
      dispatch(savePersonalDataByKey({ key: 'cpf', value: cpf }));
    }
  }, [isValid, cpf]);

  useEffect(() => {
    if (verifyData) {
      onUserVerified(verifyData);
    }
  }, [verifyData]);

  useEffect(() => {
    if (verifyError) {
      dynamicCustomEventTracking.send({ dynamicEventName: 'journey_quick_access_error_cpf_not_found_ag' });
      form.setError('oyana.cpf', { type: 'notFound', message: model.NOT_FOUND_MESSAGE });
    }
  }, [verifyError]);

  const onCpfChange = (value: string) => {
    setCpf(value);
    form.clearErrors('oyana.cpf');
  };

  const onValidityChange = (valid: boolean) => {
    setIsValid(valid);
  };

  const handleRequestCode = () => {
    dynamicCustomEventTracking.send({ dynamicEventName: 'journey_quick_access_cpf_continue_ag' });
    verifyUser({ cpf: model.sanitizeCpf(cpf) });
  };

  const handleContinueWithoutLogin = () => {
    onContinueWithoutLogin && onContinueWithoutLogin();
  };

  return {
    form,
    hasError,
    isLoading,
    requestCodeLabel: model.getRequestCodeLabel(isLoading),
    isRequestCodeDisabled: !model.canRequestCode(isValid, isLoading),
    onCpfChange,
    onValidityChange,
    handleRequestCode,
    handleContinueWithoutLogin
  };
}
